import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { appRoute } from 'shared/constants/routes';
import {
  AddDomainResponse,
  AssignRequest,
  CancelLicenseRequest,
  CancelLicenseResponse,
  PaidStatus,
  Project,
  ResumeLicenseRequest,
  ResumeLicenseResponse,
  SubscriptionsResponse,
  TerminateLicenseRequest,
  UpdateStripeLanguage,
  UpdateStripeLanguageResponse,
  UserInfoResponse,
  UserTypeResponse,
} from './apiTypes';
import { emptyApiSlice } from './emptyApiSlice';

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

export const userApiSlice = emptyApiSlice.injectEndpoints({
  endpoints: builder => ({
    getUserInfo: builder.query<UserInfoResponse, void>({
      query: () => ({
        url: '/xovi/domains',
        method: 'GET',
      }),
      transformResponse: (raw: UserInfoResponse, meta: FetchBaseQueryMeta) => {
        if (
          meta.response?.status === 204 &&
          window.location.pathname !== appRoute('/add-domain')
        ) {
          window.location.href = appRoute('/add-domain');
        }

        if (
          raw?.activeDomains.length === 0 &&
          window.location.pathname !== appRoute('/add-domain')
        ) {
          window.location.href = appRoute('/add-domain');
        }

        if (!isStandalone && raw?.csrf) {
          try {
            sessionStorage.setItem('csrfToken', raw.csrf);
          } catch (_) {}
        }

        // Normalize legacy boolean paid flags to PaidStatus enum
        const normalizePaid = (p: any): Project => ({
          ...p,
          paid:
            typeof p.paid === 'boolean'
              ? p.paid
                ? PaidStatus.PAID
                : PaidStatus.TRIAL
              : (p.paid as PaidStatus),
          // Recompute deprecated reportsAvailable from first report status
          reportsAvailable:
            p.reports && p.reports.length > 0
              ? p.reports[0].status === 'READY'
              : false,
        });
        return {
          ...raw,
          activeDomains: raw?.activeDomains.map(normalizePaid),
        } as UserInfoResponse;
      },
      providesTags: ['UserInfo'],
      // Poll until all activeDomains have reportsAvailable=true (stop early if unsubscribed)
      async onCacheEntryAdded(
        _arg,
        { cacheDataLoaded, cacheEntryRemoved, getCacheEntry, dispatch }
      ) {
        // userInfo polling lifecycle initialized
        const pollIntervalMsIncomplete = 10000; // fast cadence while waiting for reports
        const pollIntervalMsComplete = 30000; // slow cadence once everything is ready
        const maxConsecutiveIncompleteAttempts = 60; // safety cap per continuous incomplete phase (~10 min)
        const isAuthOrRedirectError = (entry: any) => {
          if (!entry || !entry.error) return false;
          const status = entry.error.status;
          return status === 302 || status === 401 || status === 403;
        };
        try {
          await cacheDataLoaded; // wait for initial data
          // initial cache data loaded
          if (isAuthOrRedirectError(getCacheEntry())) {
            window.location.href = 'https://suite.xovi.net/user/login';
            return;
          }
          let consecutiveIncompleteAttempts = 0;
          const allReportsAvailable = (data?: UserInfoResponse) =>
            !!data &&
            data.activeDomains.every(d => d.reports?.[0]?.status !== 'BUSY');
          while (true) {
            const current = getCacheEntry().data as
              | UserInfoResponse
              | undefined;
            if (isAuthOrRedirectError(getCacheEntry())) {
              window.location.href = 'https://suite.xovi.net/user/login';
              break;
            }
            const complete = allReportsAvailable(current);
            const sleepMs = complete
              ? pollIntervalMsComplete
              : pollIntervalMsIncomplete;

            const raceResult = await Promise.race([
              cacheEntryRemoved,
              new Promise(res => setTimeout(res, sleepMs)),
            ]);
            if (raceResult !== undefined) {
              break; // subscription gone
            }

            // Re-read after delay (could have been mutated externally)
            const latest = getCacheEntry().data as UserInfoResponse | undefined;
            if (isAuthOrRedirectError(getCacheEntry())) {
              break;
            }
            const nowComplete = allReportsAvailable(latest);

            if (nowComplete) {
              if (!complete) {
                dispatch(userApiSlice.util.invalidateTags(['Offerings']));
              }
              // Reset attempts so if later (e.g., after purchase) domains become incomplete again we restart fast polling
              consecutiveIncompleteAttempts = 0;
              // Stay in loop (do NOT break) so future invalidations / purchases resume polling
              continue;
            }

            consecutiveIncompleteAttempts += 1;
            if (
              consecutiveIncompleteAttempts > maxConsecutiveIncompleteAttempts
            ) {
              // After cap, fall back to slow cadence without invalidations until something externally invalidates
              consecutiveIncompleteAttempts = 0;
              continue;
            }
            dispatch(userApiSlice.util.invalidateTags(['UserInfo']));
          }
        } catch (_) {
          // swallow errors; polling is best-effort
        }
      },
    }),
    addDomain: builder.mutation<AddDomainResponse, string>({
      query: domain => {
        const normalizedDomain = domain
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '');
        const language = localStorage.getItem('locale')?.trim();
        let url = '/xovi/domains/add';
        if (language || isStandalone) {
          url += `?lang=${language ? encodeURIComponent(language) : 'en'}`;
        }
        return {
          url,
          method: 'POST',
          body: { domain: normalizedDomain },
        };
      },
      invalidatesTags: ['UserInfo'],
    }),
    removeDomain: builder.mutation<void, string>({
      query: domain => ({
        url: `/xovi/domains/${domain}/remove`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['UserInfo'],
    }),
    getSubscriptions: builder.query<SubscriptionsResponse, boolean | undefined>(
      {
        query: showTrial => ({
          url: showTrial
            ? 'subscriptions/get?show_trial=1'
            : 'subscriptions/get',
          method: 'GET',
        }),
        providesTags: ['Subscriptions'],
      }
    ),
    getStandaloneSubscriptions: builder.query<
      SubscriptionsResponse,
      boolean | undefined
    >({
      query: showTrial => ({
        url: showTrial
          ? 'subscriptions/get?show_trial=1&type=standalone'
          : 'subscriptions/get?type=standalone',
        method: 'GET',
      }),
      providesTags: ['Subscriptions'],
    }),

    assignDomain: builder.mutation<{ errors: string[] }, AssignRequest>({
      query: ({ domain }) => {
        const language = localStorage.getItem('locale')?.trim();
        let url = 'subscriptions/assign';
        if (language || isStandalone) {
          url += `?lang=${language ? encodeURIComponent(language) : 'en'}`;
        }
        return {
          url,
          method: 'POST',
          body: { domain },
        };
      },
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    unassignDomain: builder.mutation<{ errors: string[] }, AssignRequest>({
      query: ({ domain }) => ({
        url: 'subscriptions/unassign',
        method: 'POST',
        body: { domain },
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    terminateLicense: builder.mutation<void, TerminateLicenseRequest>({
      query: ({ license_id }) => ({
        url: `licenses/ka/terminate/${license_id}`,
        method: 'GET',
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    cancelLicense: builder.mutation<
      CancelLicenseResponse,
      CancelLicenseRequest
    >({
      query: ({ license_id }) => ({
        url: `subscriptions/${license_id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    resumeLicense: builder.mutation<
      ResumeLicenseResponse,
      ResumeLicenseRequest
    >({
      query: ({ license_id }) => ({
        url: `subscriptions/${license_id}/resume`,
        method: 'POST',
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    submitLicense: builder.mutation<void, { code: string }>({
      query: ({ code }) => ({
        url: `licenses/activate`,
        method: 'POST',
        body: { code: code },
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions', 'UserType'],
    }),
    terminateDomains: builder.mutation<void, string[]>({
      query: domains => ({
        url: `/xovi/domains/terminate`,
        method: 'POST',
        body: { domains: domains },
      }),
      invalidatesTags: ['UserInfo', 'Subscriptions'],
    }),
    updateStripeLanguage: builder.mutation<
      UpdateStripeLanguageResponse,
      UpdateStripeLanguage
    >({
      query: ({ lang_id }) => ({
        url: '/user-settings/update-language',
        method: 'POST',
        body: { lang_id },
      }),
    }),
    getUserType: builder.query<UserTypeResponse, void>({
      query: () => ({
        url: 'xovi/user-info',
        method: 'GET',
      }),
      providesTags: ['UserType'],
    }),
  }),
});

export const {
  useGetUserInfoQuery,
  useAddDomainMutation,
  useRemoveDomainMutation,
  useTerminateLicenseMutation,
  useCancelLicenseMutation,
  useResumeLicenseMutation,
  useAssignDomainMutation,
  useUnassignDomainMutation,
  useGetSubscriptionsQuery,
  useGetStandaloneSubscriptionsQuery,
  useSubmitLicenseMutation,
  useTerminateDomainsMutation,
  useLazyGetUserInfoQuery,
  useUpdateStripeLanguageMutation,
  useGetUserTypeQuery,
} = userApiSlice;
